import { useState } from "react";
import UserAvatar from "@/components/shared/UserAvatar";
import useUserAvatar from "@/middleware/hooks/useUserAvatar";
import { Button } from "@primitives/button";
import { createDMChat } from "@/middleware/chats";
import { useNavigationState, ActiveNavMenu } from "@/zustand/NavigationState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@primitives/dialog";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";

interface UserProfileDialogProps {
  username: string;
  avatarId?: string;
  children: React.ReactElement;
}

export default function UserProfileDialog({
  username,
  avatarId = "",
  children,
}: UserProfileDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const resolvedAvatarId = useUserAvatar(username, avatarId);
  const [open, setOpen] = useState(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const setActiveNavMenu = useNavigationState(
    (state) => state.setActiveNavMenu,
  );

  const handleContact = async () => {
    try {
      setIsCreatingChat(true);
      await createDMChat(username);
      setOpen(false);
      setActiveNavMenu(ActiveNavMenu.P2P_CHATS);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setIsCreatingChat(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="mb-4 text-center">
            {t("userProfile")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center space-y-4">
          <div className="flex justify-center">
            <UserAvatar
              username={username}
              fallbackAttachmentId={resolvedAvatarId}
              className="size-64 text-xl"
            />
          </div>
          <div className="text-lg font-medium">{username}</div>
        </div>
        <DialogFooter className="mt-6">
          <Button
            onClick={handleContact}
            className="w-full"
            disabled={isCreatingChat}
          >
            {isCreatingChat ? t("creatingChat") : t("contact")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
